import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../constants';

const ArticleCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  // Static articles data matching the design from the image
  const articles = [
    {
      id: 1,
      image: '/images/home-banner-1.webp',
      title: 'How to create a successful lead generation campaign',
      excerpt: 'What is a lead generation campaign? A Lead generation campaign is the...',
      link: '/how-to-create-a-successful-lead-generation-campaign'
    },
    {
      id: 2,
      image: '/images/LJM-Blog-Images-10.webp',
      title: 'Optimise your sales team\'s performance with good data management',
      excerpt: 'Sales teams today have more data to process than ever before. With...',
      link: '/data-management-sales-team'
    },
    {
      id: 3,
      image: '/images/LJM-Blog-Images-7.webp',
      title: 'What is lead generation, and what are the costs?',
      excerpt: 'Lead generation is essential for sustainable growth and is the first step…',
      link: '/what-is-lead-generation-and-costs'
    },
    {
      id: 4,
      image: '/images/LJM-Blog-Images-9.webp',
      title: 'Where can I acquire customer data to build my business?',
      excerpt: 'Customer data lists are essential to all industries who are looking to…',
      link: '/customer-data-online/'
    },
    {
      id: 5,
      image: '/images/LJM-Blog-Images-8.webp',
      title: 'The dos and don\'ts of direct mail marketing campaigns',
      excerpt: 'Using direct mail marketing has a wide range of advantages and can…',
      link: '/direct-mail-marketing-tips/'
    },
    {
      id: 6,
      image: '/images/LJM-Blog-Images-5.webp',
      title: 'What are the most common data quality issues?',
      excerpt: 'In today\'s data driven world, high quality data is key to success…',
      link: '/most-common-data-issues/'
    },
    {
      id: 8,
      image: '/images/data-quality-why-is-it-important.webp',
      title: 'A quick guide on data quality [why is it important?]',
      excerpt:'So, first things first. What is data quality?…',
      link: '/data-quality/'
    },
    {
      id: 9,
      image: '/images/LJM-Blog-Images-2.webp',
      title: 'What does good data management look like?',
      excerpt: 'In marketing, having a data management plan is crucial for all organisation…',
      link: '/good-data-management/'
    }
  ];

  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  const totalSlides = Math.ceil(articles.length / itemsPerSlide);

  // Update items per slide based on screen size
  useEffect(() => {
    const updateItemsPerSlide = () => {
      if (window.innerWidth < 768) { // mobile
        setItemsPerSlide(1);
      } else { // desktop and tablet
        setItemsPerSlide(3);
      }
    };

    updateItemsPerSlide();
    window.addEventListener('resize', updateItemsPerSlide);
    return () => window.removeEventListener('resize', updateItemsPerSlide);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 1) return;

    // eslint-disable-next-line no-undef
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);

    // eslint-disable-next-line no-undef
    return () => clearInterval(interval);
  }, [totalSlides, isAutoPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const getCurrentArticles = () => {
    const startIndex = currentSlide * itemsPerSlide;
    return articles.slice(startIndex, startIndex + itemsPerSlide);
  };

  return (
    <section
      className="py-8 md:py-16 px-4"
      style={{ backgroundColor: COLORS.secondary }}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="max-w-7xl mx-auto">

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          {totalSlides > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 md:p-3 shadow-lg transition-all duration-300 hover:shadow-xl"
                style={{
                  backgroundColor: '#db0058',
                  borderColor: '#db0058',
                  transform: 'translateY(-50%) translateX(-10px)'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#b8004a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#db0058'}
                aria-label="Previous slide"
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 md:p-3 shadow-lg transition-all duration-300 hover:shadow-xl"
                style={{
                  backgroundColor: '#db0058',
                  borderColor: '#db0058',
                  transform: 'translateY(-50%) translateX(10px)'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#b8004a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#db0058'}
                aria-label="Next slide"
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 px-4 md:px-8">
            {getCurrentArticles().map((article) => (
              <div
                key={article.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full"
              >
                {/* Article Image */}
                <div className="relative overflow-hidden h-40 md:h-48 flex-shrink-0">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>

                {/* Article Content */}
                <div className="p-4 md:p-6 flex flex-col flex-grow">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 md:mb-6 text-sm leading-relaxed flex-grow line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="mt-auto">
                    <Link
                      to={article.link}
                      onClick={handleScrollToTop}
                      className="inline-flex items-center text-gray-900 font-semibold text-sm hover:text-pink-600 transition-colors duration-300 group"
                    >
                      Read More
                      <svg
                        className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          {totalSlides > 1 && (
            <div className="flex justify-center mt-6 md:mt-12 space-x-2 md:space-x-3">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index
                      ? 'scale-110'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  style={currentSlide === index ? { backgroundColor: '#db0058' } : {}}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ArticleCarousel;